/**
 * KB read heatmaps — TWO grids, rendered at the top of the KB Usage tab.
 *
 * This was a per-person view on its own `/reads` page, then a single
 * project-wide total with no identity at all, and is now split relative to
 * WHOEVER IS LOOKING: your own reads in one grid, everyone else's in the other.
 * The split is decided server-side from the credentials on the request, so the
 * browser is never told who anybody is and no name reaches this module.
 *
 * Every function here is a pure input→HTML builder: no fetches, no module
 * state, no DOM. State lives in kb_usage.ts and is passed in. That split is what
 * makes the grids cheap to pin in a test with a fixed dataset.
 *
 * CSS: styles/views/kb-heatmap.css
 */

import { esc } from '../../shared/rendering.js'
import {
  DAYS_PER_WEEK,
  MONTHS,
  WEEKS,
  addDays,
  gridStart,
  longDate,
  todayUTC,
  utcDay,
} from './kb_dates.js'
import { action } from '../components/action-delegation.js'
import type { KBReadHeatmapRow, KBReadDetailRow } from '../../api/insights.js'

// Re-exported so existing importers keep one place to reach for these.
export {
  DAYS_PER_WEEK,
  WEEKS,
  WINDOW_DAYS,
  addDays,
  gridStart,
  longDate,
  todayUTC,
  utcDay,
} from './kb_dates.js'

/** KB types offered as filter chips. Mirrors the server's KB_TYPES. */
export const KB_TYPES = [
  'adr',
  'learning',
  'pattern',
  'convention',
  'decision',
  'debt',
  'antipattern',
  'insight',
  'procedure',
  'invariant',
] as const

// ─── Grouping ───────────────────────────────────────────────────────

/** day → total reads. */
export function byDay(rows: readonly KBReadHeatmapRow[]): Map<string, number> {
  const out = new Map<string, number>()
  for (const r of rows) out.set(r.day, (out.get(r.day) ?? 0) + r.reads)
  return out
}

export function totalReads(rows: readonly KBReadHeatmapRow[]): number {
  return rows.reduce((n, r) => n + r.reads, 0)
}

/**
 * Five intensity buckets, matching the ramp in kb-heatmap.css.
 *
 * ABSOLUTE thresholds, not a share of the busiest day: a relative scale would
 * repaint the whole grid whenever one outlier day appeared, so the same colour
 * would mean different things on different visits.
 */
export function bucket(reads: number): number {
  if (reads <= 0) return 0
  if (reads <= 2) return 1
  if (reads <= 5) return 2
  if (reads <= 10) return 3
  return 4
}

// ─── Rendering ──────────────────────────────────────────────────────

export function renderFilterChips(active: string | null): string {
  const chip = (value: string | null, label: string): string => {
    const on = active === value
    return `<button class="rd-chip${on ? ' active' : ''}" aria-pressed="${on}" ${action('setReadsType', value ?? 'null')}>${esc(label)}</button>`
  }
  return (
    `<div class="rd-chips" role="group" aria-label="Filter heatmap by KB type">` +
    chip(null, 'All types') +
    KB_TYPES.map((t) => chip(t, t)).join('') +
    `</div>`
  )
}

/** Month labels above the grid, one per column where the month changes. */
export function renderMonthLabels(start: Date): string {
  let last = -1
  const cells: string[] = []
  for (let w = 0; w < WEEKS; w++) {
    const m = addDays(start, w * DAYS_PER_WEEK).getUTCMonth()
    if (m !== last) {
      cells.push(`<span class="rd-month" style="grid-column:${w + 1}">${MONTHS[m]}</span>`)
      last = m
    }
  }
  return `<div class="rd-months" aria-hidden="true">${cells.join('')}</div>`
}

function renderCells(
  counts: Map<string, number>,
  start: Date,
  open: string | null,
  today: string,
  scope: HeatmapScope,
) {
  const cells: string[] = []
  for (let w = 0; w < WEEKS; w++) {
    for (let d = 0; d < DAYS_PER_WEEK; d++) {
      const day = utcDay(addDays(start, w * DAYS_PER_WEEK + d))
      // The grid is a rectangle; the calendar is not. Days after today are
      // holes, so "no reads yet" is never confused with "the future".
      if (day > today) {
        cells.push(`<span class="rd-cell rd-future" aria-hidden="true"></span>`)
        continue
      }
      const reads = counts.get(day) ?? 0
      // The subject is part of the label because there are TWO grids now: the
      // same date appears in both, and "13 reads on 27 May" said nothing about
      // which of them a screen-reader user had just landed on.
      const whose = scope === 'mine' ? 'you' : 'the team'
      const label = `${reads} read${reads === 1 ? '' : 's'} by ${whose} on ${longDate(day)}`
      // Zero-read days are not interactive — there is no evidence to open.
      const interactive = reads > 0
      cells.push(
        `<span class="rd-cell rd-b${bucket(reads)}${open === day ? ' rd-open' : ''}"` +
          ` style="grid-row:${d + 1};grid-column:${w + 1}"` +
          ` title="${esc(label)}" aria-label="${esc(label)}"` +
          (interactive
            ? ` role="button" tabindex="0" ${action('openReadsCell', `${scope}|${day}`)}`
            : ` role="img"`) +
          `></span>`,
      )
    }
  }
  return cells.join('')
}

/**
 * The drawer: evidence, not just colour.
 *
 * A wall of squares with nothing behind it gets admired once and never
 * reopened (KB learning-0e62f210230c).
 */
export function renderDrawer(
  open: string | null,
  detail: readonly KBReadDetailRow[],
  loading: boolean,
  // Named, because the cells are: every cell says "by you" or "by the team", and
  // the drawer they open should not be the one place that drops the distinction.
  whose = 'you',
): string {
  if (!open) return ''
  if (loading) {
    return `<div class="rd-drawer"><div class="loading"><div class="spinner"></div></div></div>`
  }

  const items = detail.length
    ? detail
        .map(
          (r) =>
            `<li class="rd-item">
          <a class="rd-item-title" href="#/kb/${encodeURIComponent(r.entryId)}">${esc(r.title ?? r.entryId)}</a>
          <span class="rd-item-meta">${r.category ? esc(r.category) : ''}${r.reads > 1 ? ` · ${r.reads}×` : ''}</span>
        </li>`,
        )
        .join('')
    : `<li class="rd-item rd-empty">No entries resolved for this day.</li>`

  // Built as a plain string first: a nested template inside the attribute is
  // something ST-aria-label cannot see through.
  const drawerLabel = `Entries read by ${whose} on ${longDate(open)}`
  return `<div class="rd-drawer" role="region" aria-label="${esc(drawerLabel)}">
    <header class="rd-drawer-head">
      <strong>${esc(longDate(open))}</strong>
      <div class="rd-project-list"></div>
      <button class="rd-close" ${action('closeReadsCell', '')} aria-label="Close details">×</button>
    </header>
    <ul class="rd-items">${items}</ul>
  </div>`
}

/** Which side of the viewer split a grid shows. */
export type HeatmapScope = 'mine' | 'team'

/**
 * One heatmap block: heading, grid, drawer.
 *
 * Two of these are rendered, one per side of the viewer split. The type
 * filter chips are NOT part of this - they apply to both grids at once, so they
 * are rendered by the caller above the pair rather than duplicated inside each,
 * where two sets of chips would look like two independent filters.
 */
export function renderHeatmap(
  rows: readonly KBReadHeatmapRow[],
  opts: {
    scope: HeatmapScope
    /** True when THIS grid's request failed: render no grid and no total. */
    failed?: boolean
    openDay: string | null
    openScope: HeatmapScope | null
    detail: readonly KBReadDetailRow[]
    detailLoading: boolean
    today?: string
  },
): string {
  const today = opts.today ?? utcDay(todayUTC())
  const counts = byDay(rows)
  const total = totalReads(rows)
  const start = gridStart()
  const isMine = opts.scope === 'mine'

  // Headings name their own subject rather than relying on order: two grids
  // labelled "Read activity" and "Read activity" would be a coin toss.
  const name = isMine ? 'Your read activity' : 'Team read activity'
  const sub = isMine
    ? `Entries you read · last ${WEEKS} weeks (UTC)`
    : `Everyone else on this project · last ${WEEKS} weeks (UTC)`

  // A real caption: without it the grid is an unlabelled wall of colour to a
  // screen reader.
  const caption = opts.failed
    ? `${name} — could not be loaded`
    : `${name} — ${total} read${total === 1 ? '' : 's'} in the last ${WEEKS} weeks`

  // A failed grid renders NO grid and NO total. Rendering it from [] emitted
  // "0 reads" and an aria-label saying "0 reads in the last 52 weeks", so a
  // screen-reader user got only the false assertion — the error message was a
  // sibling node with nothing tying it to either grid.
  if (opts.failed) {
    return `<section class="rd-heatmap" aria-label="${esc(caption)}">
      <header class="rd-person-head">
        <div>
          <h2 class="rd-name">${esc(name)}</h2>
          <p class="rd-sub">${esc(sub)}</p>
        </div>
      </header>
      <div class="empty">Could not load this read activity.</div>
    </section>`
  }

  // The drawer belongs to whichever grid was clicked; the other renders none,
  // so a day cannot appear open in both at once.
  const openHere = opts.openScope === opts.scope ? opts.openDay : null

  return `<section class="rd-heatmap" aria-label="${esc(caption)}">
    <header class="rd-person-head">
      <div>
        <h2 class="rd-name">${esc(name)}</h2>
        <p class="rd-sub">${esc(sub)}</p>
      </div>
      <span class="rd-total">${total} read${total === 1 ? '' : 's'}</span>
    </header>
    <div class="rd-legend" aria-hidden="true">
      <span>Less</span>
      <span class="rd-cell rd-b0"></span><span class="rd-cell rd-b1"></span>
      <span class="rd-cell rd-b2"></span><span class="rd-cell rd-b3"></span>
      <span class="rd-cell rd-b4"></span>
      <span>More</span>
    </div>
    ${renderMonthLabels(start)}
    <div class="rd-grid">${renderCells(counts, start, openHere, today, opts.scope)}</div>
    ${renderDrawer(openHere, opts.detail, opts.detailLoading, isMine ? 'you' : 'the team')}
  </section>`
}
